import React, { createContext, useContext, useEffect, useState } from "react";
import jwt_decode from "jwt-decode";
import * as userAPI from "./user";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [authType, setAuthType] = useState('jwt'); // 'jwt' or 'oauth'
  
  // Check for tokens in localStorage
  const jwtToken = localStorage.getItem("jwt");
  const oauthToken = localStorage.getItem("oauth_token");
  const token = oauthToken || jwtToken;

  // Regular login with username/password
  const login = (user, token) => {
    localStorage.setItem("jwt", token);
    localStorage.removeItem("oauth_token");
    setAuthType('jwt');
    setCurrentUser(user);
  };
  
  // OAuth login
  const oauthLogin = (tokens, userInfo) => {
    localStorage.setItem("oauth_token", tokens.access_token);
    localStorage.removeItem("jwt");
    setAuthType('oauth');
    setCurrentUser(userInfo);
  };

  const logout = () => {
    localStorage.removeItem("jwt");
    localStorage.removeItem("oauth_token");
    setCurrentUser(null);
    setAuthType('jwt');
  };

  const isAuthenticated = () => {
    if (isLoading) return false;
    
    if (currentUser !== null) {
      return true;
    }
    
    if (jwtToken) {
      try {
        const decoded = jwt_decode(jwtToken);
        return decoded.exp > Date.now() / 1000;
      } catch (e) {
        return false;
      }
    }
    
    if (oauthToken) {
      // For OAuth tokens, we'll rely on the API to tell us if it's expired
      // The backend will return 401 if the token is invalid
      return true;
    }

    return false;
  };

  const value = {
    currentUser,
    isLoading,
    authType,
    login,
    oauthLogin,
    logout,
    isAuthenticated,
  };

  useEffect(() => {
    const getUserInfo = async () => {
      try {
        // If we have a token, try to get the user info
        if (token) {
          const user = await userAPI.getUserInfo();
          setCurrentUser(user);
          setAuthType(oauthToken ? 'oauth' : 'jwt');
        } else {
          setCurrentUser(null);
        }
        setLoading(false);
      } catch (err) {
        // If we get a 401, the token is invalid
        if (err.status && err.status === 401) {
          setLoading(false);
          setCurrentUser(null);
          logout();
        }
      }
    };
    
    getUserInfo();
  }, [token, jwtToken, oauthToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
